import React from "react";

/**
 * Event titles arrive with a little HTML in them.
 *
 * Measured, not guessed: of 77,427 events, 4,167 titles contain markup and the
 * ONLY tag among them is `<del>` — 5,562 uses, all of the form
 * `CANCELLED - <del>Squad 1</del>`. The rest are numeric entities picked up
 * from the source system: `&#8217;` for an apostrophe, `&#235;` for ë,
 * `&#8211;` for an en dash.
 *
 * Rendered by building React elements rather than by handing a string to
 * `dangerouslySetInnerHTML`. Titles come from a sync we do not control, so the
 * alternative would be trusting them or adding a sanitiser to scrub them; an
 * allow-list that can only ever emit a handful of inline elements has no
 * injection surface to scrub in the first place. `<script>`, `<img onerror>`
 * and friends are not "cleaned" here — they are simply not things this function
 * can produce.
 *
 * Anything outside the allow-list is dropped, keeping its text. A title of
 * `<b>Sports <i>Day</i></b>` renders bold and italic; `<a href=...>Sports</a>`
 * renders as the word Sports.
 */

/** Inline formatting that may appear in a title. Everything else is stripped. */
const ALLOWED: Record<string, keyof React.JSX.IntrinsicElements> = {
	del: "del",
	s: "s",
	strike: "s",
	b: "b",
	strong: "strong",
	i: "i",
	em: "em",
	u: "u",
	sub: "sub",
	sup: "sup",
};

/** Void tags worth honouring; a title is one line, so only `<br>`. */
const VOID_TAGS = new Set(["br"]);

/**
 * Tags whose TEXT is dropped along with the tag.
 *
 * Every other unknown tag keeps its text — `<a href=…>Sports</a>` should still
 * read "Sports". Script and style are the exception: their contents are code,
 * not words, so keeping them would print `window.alert(1)` in the middle of a
 * title. Nothing here executes either way; this is about not showing rubbish.
 */
const DROP_CONTENT = new Set(["script", "style"]);

const NAMED_ENTITIES: Record<string, string> = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: '"',
	apos: "'",
	nbsp: " ",
};

/**
 * Turn entities back into characters.
 *
 * Done by hand rather than through the usual `textarea.innerHTML` trick: that
 * asks the HTML parser to do it, which is the very thing this file avoids, and
 * it does not work outside a browser (a test, a future server render).
 */
export const decodeEntities = (text: string): string =>
	text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, body: string) => {
		if (body[0] === "#") {
			const code =
				body[1] === "x" || body[1] === "X"
					? parseInt(body.slice(2), 16)
					: parseInt(body.slice(1), 10);
			// Anything unparseable or outside Unicode is left as written, which
			// is more honest than substituting a replacement character.
			return Number.isFinite(code) && code > 0 && code <= 0x10ffff
				? String.fromCodePoint(code)
				: whole;
		}
		return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
	});

/** Matches one tag: `</?name ...>`. Attributes are captured only to discard. */
const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;

/**
 * The title as React nodes, with its formatting kept.
 *
 * Unbalanced markup is tolerated — a stray `</del>` with no opener is ignored,
 * and an unclosed `<del>` simply runs to the end. Titles are hand-edited in
 * another system; refusing to render one because its tags do not balance would
 * be the wrong call.
 */
export function renderTitle(title: string | undefined): React.ReactNode {
	const text = title ?? "";
	if (!text) return "";
	// Nothing to parse: the overwhelmingly common case.
	if (!text.includes("<") && !text.includes("&")) return text;

	type Frame = {
		tag: keyof React.JSX.IntrinsicElements | null;
		kids: React.ReactNode[];
	};
	const stack: Frame[] = [{ tag: null, kids: [] }];
	let cursor = 0;
	let key = 0;

	const pushText = (raw: string) => {
		if (raw) stack[stack.length - 1].kids.push(decodeEntities(raw));
	};

	TAG.lastIndex = 0;
	let match: RegExpExecArray | null;
	while ((match = TAG.exec(text)) !== null) {
		pushText(text.slice(cursor, match.index));
		cursor = TAG.lastIndex;

		const closing = match[1] === "/";
		const name = match[2].toLowerCase();

		if (VOID_TAGS.has(name)) {
			if (!closing) stack[stack.length - 1].kids.push(<br key={key++} />);
			continue;
		}

		if (DROP_CONTENT.has(name)) {
			if (!closing) {
				// Skip to the matching close, or to the end if there is none.
				const close = text.toLowerCase().indexOf(`</${name}`, cursor);
				const end = close === -1 ? text.length : close;
				cursor = end;
				TAG.lastIndex = end;
			}
			continue;
		}

		const element = ALLOWED[name];
		if (!element) continue; // Unknown tag: dropped, its text kept.

		if (!closing) {
			stack.push({ tag: element, kids: [] });
			continue;
		}

		/* Close the nearest matching opener, so a stray closer changes nothing.
		   A hand-rolled reverse scan rather than `findLastIndex`, which needs
		   an es2023 lib this project does not target. */
		let at = -1;
		for (let i = stack.length - 1; i > 0; i--) {
			if (stack[i].tag === element) {
				at = i;
				break;
			}
		}
		if (at <= 0) continue;
		while (stack.length > at) {
			const frame = stack.pop() as Frame;
			const Tag = frame.tag as keyof React.JSX.IntrinsicElements;
			stack[stack.length - 1].kids.push(
				React.createElement(Tag, { key: key++ }, ...frame.kids),
			);
		}
	}
	pushText(text.slice(cursor));

	// Anything left open runs to the end of the title.
	while (stack.length > 1) {
		const frame = stack.pop() as Frame;
		const Tag = frame.tag as keyof React.JSX.IntrinsicElements;
		stack[stack.length - 1].kids.push(
			React.createElement(Tag, { key: key++ }, ...frame.kids),
		);
	}

	return <>{stack[0].kids}</>;
}

/**
 * The title as plain text: tags removed, entities decoded.
 *
 * For the places a string is required rather than markup — an accessible name,
 * a `title` attribute, the text a search matches against. Searching should hit
 * "Squad" in `<del>Squad 1</del>` and should NOT hit "del".
 */
export function titleText(title: string | undefined): string {
	const text = title ?? "";
	if (!text) return "";
	const stripped = text
		// Script and style contents go with the tag, as in renderTitle.
		.replace(/<(script|style)\b[^>]*>[\s\S]*?(<\/\1\s*>|$)/gi, "")
		// A line break is a word boundary: "Line<br>break" is two words, and
		// running them together would break a search for either.
		.replace(/<br\b[^>]*>/gi, " ")
		.replace(TAG, "");
	return decodeEntities(stripped).replace(/\s+/g, " ").trim();
}
